import { useEffect, useState } from 'react';
import {
  Mail,
  MailOpen,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Inbox,
} from 'lucide-react';
import {
  apiContactListMessages,
  apiContactMarkRead,
  apiContactDeleteMessage,
} from '../api/client';

// ── helpers ────────────────────────────────────────────────────────────────

function formatDate(utcStr) {
  try {
    return new Date(utcStr).toLocaleString(undefined, {
      year:   'numeric',
      month:  'short',
      day:    'numeric',
      hour:   '2-digit',
      minute: '2-digit',
    });
  } catch {
    return utcStr;
  }
}

// ── Single message row ─────────────────────────────────────────────────────

function MessageRow({ msg, onMarkRead, onDelete, t }) {
  const [expanded, setExpanded]       = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [confirmDel, setConfirmDel]   = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const handleMarkRead = async () => {
    if (msg.isRead) return;
    setMarkingRead(true);
    await onMarkRead(msg.id);
    setMarkingRead(false);
  };

  const handleDelete = async () => {
    if (!confirmDel) { setConfirmDel(true); return; }
    setDeleting(true);
    await onDelete(msg.id);
    setDeleting(false);
  };

  return (
    <article
      className={`rounded-2xl border transition ${
        msg.isRead
          ? 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
          : 'border-brand-200 bg-brand-50/40 dark:border-brand-800/60 dark:bg-brand-950/20'
      }`}
    >
      {/* Header row */}
      <div
        className="flex cursor-pointer items-start gap-4 p-5"
        onClick={() => {
          setExpanded((v) => !v);
          if (!msg.isRead) void handleMarkRead();
        }}
      >
        {/* Icon */}
        <span className={`mt-0.5 shrink-0 ${msg.isRead ? 'text-slate-400' : 'text-brand-500'}`}>
          {msg.isRead ? <MailOpen size={18} /> : <Mail size={18} />}
        </span>

        {/* Content preview */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">
              {msg.name}
            </span>
            <span className="text-xs text-slate-400">{msg.email}</span>
            {!msg.isRead && (
              <span className="rounded-full bg-brand-500 px-2 py-0.5 text-xs font-semibold text-white">
                {t('contactNew')}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
            {msg.subject}
          </p>
          {!expanded && (
            <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
              {msg.body}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-400">{formatDate(msg.createdUtc)}</p>
        </div>

        {/* Toggle chevron */}
        <span className="shrink-0 text-slate-400">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4 dark:border-slate-800">
          <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700 dark:text-slate-200">
            {msg.body}
          </p>

          <div className="mt-4 flex gap-2">
            {/* Mark read button (only if unread) */}
            {!msg.isRead && (
              <button
                type="button"
                onClick={handleMarkRead}
                disabled={markingRead}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {markingRead ? <RefreshCw size={12} className="animate-spin" /> : <MailOpen size={12} />}
                {t('contactMarkRead')}
              </button>
            )}

            {/* Reply via email */}
            <a
              href={`mailto:${msg.email}?subject=Re: ${encodeURIComponent(msg.subject)}`}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              <Mail size={12} />
              {t('contactReply')}
            </a>

            {/* Delete */}
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={`ms-auto inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${
                confirmDel
                  ? 'bg-pink-600 text-white hover:bg-pink-700'
                  : 'border border-slate-200 text-slate-500 hover:border-pink-300 hover:text-pink-600 dark:border-slate-700 dark:text-slate-400'
              }`}
            >
              {deleting ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
              {confirmDel ? t('superAdminConfirmDelete') : t('superAdminDelete')}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

function ContactMessagesPage({ t, onToast }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all'); // 'all' | 'unread' | 'read'

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiContactListMessages();
      setMessages(data);
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : t('contactLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleMarkRead = async (id) => {
    try {
      await apiContactMarkRead(id);
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, isRead: true } : m));
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : t('contactMarkReadError'), 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiContactDeleteMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      onToast?.(t('contactDeletedToast'), 'success');
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : t('contactDeleteError'), 'error');
    }
  };

  const filtered = messages.filter((m) => {
    if (filter === 'unread') return !m.isRead;
    if (filter === 'read')   return  m.isRead;
    return true;
  });

  const unreadCount = messages.filter((m) => !m.isRead).length;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#1A1A1A] via-brand-600 to-green-500 p-6 text-white shadow-xl shadow-brand/20 dark:border-slate-800">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold">
              <Mail size={15} />
              {t('navContactMessages')}
            </div>
            <h1 className="text-3xl font-bold">{t('contactTitle')}</h1>
            <p className="mt-2 text-sm text-brand-50">{t('contactSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/20 disabled:opacity-60"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            {t('refresh')}
          </button>
        </div>
      </section>

      {/* Stats + filter */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-3">
          {[
            { key: 'all',    label: t('contactFilterAll'),    count: messages.length },
            { key: 'unread', label: t('contactFilterUnread'), count: unreadCount },
            { key: 'read',   label: t('contactFilterRead'),   count: messages.length - unreadCount },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                filter === f.key
                  ? 'bg-brand-600 text-white shadow'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              {f.label}
              <span className={`rounded-full px-1.5 py-0.5 text-xs ${filter === f.key ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700'}`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>
        {unreadCount > 0 && (
          <span className="flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400">
            <AlertCircle size={15} />
            {unreadCount} {t('contactUnreadHint')}
          </span>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-slate-300 py-16 dark:border-slate-700">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
            <Inbox size={26} />
          </span>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('contactEmpty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((msg) => (
            <MessageRow
              key={msg.id}
              msg={msg}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ContactMessagesPage;
